import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useTranslation } from '../../../i18n';

import { actions, StepType } from '../../../entities/rates';
import * as providersActs from '../../../entities/providers';
import { getActiveStep, isRuleAccepted } from '../../../entities/rates/selectors';
import { ratesModelSelector } from '../../../entities/providers/form.selector';
import * as fromProviders from '../../../entities/providers/selectors';

import { isFormStep, isSearchStep, isPaymentStep, processSteps } from './stepper-process';
import { getFormRules } from '../form/formRules';

import { Alert, Card, Form, Steps } from '../../element';

import { StepperStep } from './step';
import { RatesServices } from '../services';
import { RatesForm } from '../form';
import { RatesPayment } from '../payment';
import { StepperNav } from './stepper-nav';
import { scrollToFormError } from '../helpers/ui';
import { registerProviderFormRef } from '../../providers/provider-address-form/hooks';

const Container = styled(Card)`
  background: #f5f7fa;
`;

export const Stepper: React.FC = () => {
  const [t, { language }] = useTranslation(['common', 'rates']);

  const activeStep = useSelector(getActiveStep);
  const ruleAccepted = useSelector(isRuleAccepted);
  const model = useSelector(ratesModelSelector);
  const errors = useSelector(fromProviders.getFormErrors);

  const isMaintenance = useSelector(fromProviders.isMaintenance);
  const maintenanceMsg = useSelector(fromProviders.getMaintenance);

  const rules = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getFormRules(t, errors as any),
    [language, errors]
  );

  const dispatch = useDispatch();

  const formRef = useRef<InstanceType<typeof Form>>(null);

  // hook to provide access form ref for validation
  useEffect(() => {
    registerProviderFormRef(formRef);
  }, [formRef]);

  // show errors
  useEffect(() => {
    if (Object.keys(errors).length && formRef.current) {
      formRef.current.validate();
    }
  }, [rules, formRef]);

  const handleStepChange = useCallback(
    (step: number) => {
      // handle go back
      if (step === StepType.search) {
        dispatch(providersActs.bookService({ service: '' }));
      }

      dispatch(actions.setStep({ step }));
    },
    [activeStep]
  );

  const handleSubmit = useCallback(() => {
    if (activeStep === StepType.form) {
      if (formRef.current) {
        formRef.current.validate((valid) => {
          if (valid) {
            dispatch(providersActs.submitForm());
          } else {
            scrollToFormError();
          }
        });
      }

      return;
    }

    handleStepChange(activeStep + 1);
  }, [activeStep, formRef]);

  return (
    <Card className="is-hover-shadow">
      <div className="mb-20">
        {isMaintenance && (
          <Alert
            type="error"
            showIcon
            title="Maintenance"
            description={maintenanceMsg || undefined}
            className="is-light"
          />
        )}

        {!isMaintenance && (
          <Steps className="el-steps--simple">
            {processSteps.map((step, idx) => (
              <StepperStep key={step.type} icon={step.icon} active={idx === activeStep} finished={activeStep > idx} />
            ))}
          </Steps>
        )}
      </div>

      {isSearchStep(activeStep) && <RatesServices disabled={isMaintenance} />}

      {activeStep > 0 && (
        <Container>
          {isFormStep(activeStep) && (
            <Form ref={formRef} model={model} labelPosition="top" rules={rules}>
              <RatesForm />
            </Form>
          )}
          {isPaymentStep(activeStep) && <RatesPayment />}

          {activeStep > 0 && (
            <StepperNav
              total={processSteps.length}
              active={activeStep}
              nextDisabled={!ruleAccepted}
              handleNext={handleSubmit}
              onChange={handleStepChange}
            />
          )}
        </Container>
      )}
    </Card>
  );
};
